<template>
  <div class="community-page">
    <div class="community-header" :style="{ background: categoryInfo.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }">
      <div class="community-info">
        <div class="category-icon">
          <i :class="categoryInfo.icon || 'el-icon-folder'"></i>
        </div>
        <div class="category-detail">
          <h1 class="category-name">{{ categoryInfo.name }}</h1>
          <p class="category-desc">{{ categoryInfo.description || '暂无描述' }}</p>
          <div class="category-meta">
            <span><i class="el-icon-document"></i> {{ formatPostCount(postCount) }} 篇帖子</span>
            <span><i class="el-icon-user"></i> {{ memberCount }} 位成员</span>
          </div>
        </div>
      </div>
      <el-button type="warning" round size="small" @click="goPublish">
        <i class="el-icon-edit"></i> 发帖
      </el-button>
    </div>

    <div class="community-content">
      <div class="main-content">
        <div class="filter-bar">
          <el-tabs v-model="activeOrder" @tab-change="changeOrder">
            <el-tab-pane label="最新发布" name="time">
              <i class="el-icon-time"></i>
            </el-tab-pane>
            <el-tab-pane label="最热帖子" name="score">
              <i class="el-icon-fire"></i>
            </el-tab-pane>
          </el-tabs>
        </div>

        <div class="post-list" v-loading="loading" @scroll="handleScroll">
          <div class="post-item" v-for="post in postList" :key="post.docDigest || post.post_id" @click="goDetail(post.docDigest || post.post_id)">
            <div class="post-vote">
              <el-button type="text" @click.stop="vote(post.docDigest || post.post_id, 1)">
                <i class="el-icon-caret-top"></i>
              </el-button>
              <span class="vote-count">{{ post.praiseCount || 0 }}</span>
              <el-button type="text" @click.stop="vote(post.docDigest || post.post_id, -1)">
                <i class="el-icon-caret-bottom"></i>
              </el-button>
            </div>
            <div class="post-main">
              <h3 class="post-title">{{ post.title }}</h3>
              <p class="post-summary">{{ post.title }}</p>
              <div class="post-meta">
                <span class="post-author">
                  <i class="el-icon-user"></i> {{ post.createUserName }}
                </span>
                <span class="post-time">
                  <i class="el-icon-time"></i> {{ formatDate(post.createTime) }}
                </span>
                <span class="post-comments">
                  <i class="el-icon-chat-dot-round"></i> {{ post.commentCount || 0 }} 评论
                </span>
              </div>
            </div>
          </div>

          <el-empty v-if="postList.length === 0 && !loading" description="暂无帖子">
            <el-button type="primary" @click="goPublish">发表第一篇帖子</el-button>
          </el-empty>

          <div class="loading-status" v-if="loading">
            <i class="el-icon-loading"></i>
            <span>加载中...</span>
          </div>

          <el-empty v-if="postList.length > 0 && !hasMore" description="没有更多了" :image-size="80"></el-empty>
        </div>
      </div>

      <div class="sidebar">
        <div class="category-card">
          <div class="card-header">
            <i class="el-icon-folder"></i>
            <span>子分类</span>
          </div>
          <div class="sub-categories">
            <div
              v-for="sub in subCategories"
              :key="sub.id"
              class="sub-item"
              :class="{ active: sub.id === currentSubCategory }"
              @click="selectSubCategory(sub.id)"
            >
              <i :class="sub.icon || 'el-icon-folder'"></i>
              <span>{{ sub.categoryName }}</span>
            </div>
            <el-empty v-if="subCategories.length === 0" description="无子分类" :image-size="50"></el-empty>
          </div>
        </div>

        <DocRankList :categoryId="categoryId" :limit="5" />
      </div>
    </div>
  </div>
</template>

<script>
import DocRankList from './components/DocRankList.vue';

export default {
  name: 'Community',
  components: { DocRankList },
  data() {
    return {
      categoryId: this.$route.params.id,
      currentSubCategory: null,
      allSubCategoryIds: [],
      categoryInfo: {
        name: '',
        description: '',
        icon: '',
        gradient: ''
      },
      postList: [],
      subCategories: [],
      loading: false,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      hasMore: true,
      activeOrder: 'time',
      postCount: 0,
      memberCount: 0,
      scrollListener: null
    };
  },
  created() {
    this.getCategoryInfo();
    this.getPostList();
  },
  methods: {
    async getCategoryInfo() {
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/category/categoryTree',
          data: {}
        });
        
        if (response.code === '200' && response.data) {
          const category = this.findCategory(response.data, this.categoryId);
          if (category) {
            this.categoryInfo = {
              name: category.categoryName,
              description: category.description || '',
              icon: category.icon || 'el-icon-folder',
              gradient: this.getGradientByLevel(category.levelCode)
            };
            this.subCategories = category.children || [];
            // 收集所有子分类 ID（包括子分类的子分类）
            this.allSubCategoryIds = this.collectAllSubCategoryIds(category);
          }
        }
      } catch (error) {
        console.error('获取分类信息失败:', error);
      }
    },
    collectAllSubCategoryIds(category) {
      const ids = [];
      if (category.children && category.children.length > 0) {
        for (const child of category.children) {
          ids.push(child.id);
          // 递归收集子分类的子分类
          const subIds = this.collectAllSubCategoryIds(child);
          ids.push(...subIds);
        }
      }
      return ids;
    },
    findCategory(node, id) {
      if (node.id == id) return node;
      if (node.children) {
        for (const child of node.children) {
          const found = this.findCategory(child, id);
          if (found) return found;
        }
      }
      return null;
    },
    getGradientByLevel(level) {
      const gradients = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
      ];
      return gradients[(level - 1) % gradients.length] || gradients[0];
    },
    async getPostList() {
      this.loading = true;
      try {
        const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
        
        // 确定查询的分类 ID 列表
        let categoryIds = [];
        if (this.currentSubCategory) {
          categoryIds = [this.currentSubCategory];
        } else if (this.allSubCategoryIds.length > 0) {
          categoryIds = [...this.allSubCategoryIds];
        } else {
          categoryIds = [this.categoryId];
        }
        
        const response = await this.$axios({
          method: 'post',
          url: '/doc/queryUserDocListByCategories',
          data: {
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            order: this.activeOrder,
            categoryIds: categoryIds,
            token: loginResult.token || '',
            userId: loginResult.userId || '',
            userName: loginResult.userName || '',
            ip: '',
            source: 'web'
          }
        });
        
        if (response.code === '200' || response.code === 200) {
          const newList = response.data.data || [];
          this.total = response.data.total || 0;
          
          if (this.pageNum === 1) {
            this.postList = newList;
          } else {
            this.postList = [...this.postList, ...newList];
          }
          
          this.hasMore = this.postList.length < this.total;
        } else {
          this.$message.error(response.msg || '获取帖子失败');
        }
      } catch (error) {
        console.error('获取帖子失败:', error);
        this.$message.error('获取帖子失败');
      } finally {
        this.loading = false;
      }
    },
    changeOrder() {
      this.pageNum = 1;
      this.postList = [];
      this.hasMore = true;
      this.getPostList();
    },
    handlePageChange(page) {
      this.pageNum = page;
      this.getPostList();
    },
    selectSubCategory(id) {
      this.currentSubCategory = id === this.currentSubCategory ? null : id;
      this.pageNum = 1;
      this.postList = [];
      this.hasMore = true;
      this.getPostList();
    },
    handleScroll() {
      if (this.loading || !this.hasMore) return;
      
      const container = this.$el.querySelector('.post-list');
      if (!container) return;
      
      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;
      
      // 距离底部还有 300px 时开始加载
      if (scrollTop + clientHeight >= scrollHeight - 300) {
        this.pageNum++;
        this.getPostList();
      }
    },
    mounted() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.post-list');
        if (container) {
          container.addEventListener('scroll', this.handleScroll);
        }
      });
    },
    beforeDestroy() {
      const container = this.$el.querySelector('.post-list');
      if (container) {
        container.removeEventListener('scroll', this.handleScroll);
      }
    },
    async vote(docId, direction) {
      const loginResult = localStorage.getItem('loginResult');
      if (!loginResult) {
        this.$message.warning('请先登录');
        this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } });
        return;
      }

      try {
        const response = await this.$axios({
          method: 'post',
          url: '/doc/praise',
          data: {
            docDigest: docId,
            isLike: direction === 1 ? 1 : 0,
            token: JSON.parse(loginResult).token || '',
            userId: JSON.parse(loginResult).userId || '',
            userName: JSON.parse(loginResult).userName || '',
            ip: '',
            source: 'web'
          }
        });

        if (response.code === '200' || response.code === 200) {
          this.$message.success(direction === 1 ? '点赞成功' : '已取消点赞');
          this.getPostList();
        } else {
          this.$message.warning(response.msg || '操作失败');
        }
      } catch (error) {
        console.error('投票失败:', error);
        this.$message.error('操作失败');
      }
    },
    goDetail(id) {
      this.$router.push({ name: 'Content', params: { id } });
    },
    goPublish() {
      this.$router.push({ name: 'Publish', query: { categoryId: this.currentSubCategory || this.categoryId } });
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      
      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;
      
      if (diff < minute) return '刚刚';
      if (diff < hour) return Math.floor(diff / minute) + '分钟前';
      if (diff < day) return Math.floor(diff / hour) + '小时前';
      return Math.floor(diff / day) + '天前';
    },
    formatPostCount(count) {
      if (!count) return 0;
      if (count >= 10000) {
        return (count / 10000).toFixed(1) + 'w';
      } else if (count >= 1000) {
        return (count / 1000).toFixed(1) + 'k';
      }
      return count;
    }
  },
  watch: {
    '$route.params.id'(newId) {
      this.categoryId = newId;
      this.currentSubCategory = null;
      this.getCategoryInfo();
      this.getPostList();
    }
  }
};
</script>

<style lang="less" scoped>
.community-page {
  min-height: calc(100vh - 64px);
  margin-top: 64px;
  background: #f5f7fa;
}

.community-header {
  padding: 40px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #fff;

  .community-info {
    display: flex;
    align-items: center;
    gap: 20px;

    .category-icon {
      width: 80px;
      height: 80px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 40px;
      }
    }

    .category-detail {
      .category-name {
        margin: 0 0 8px 0;
        font-size: 28px;
        font-weight: 700;
      }

      .category-desc {
        margin: 0 0 12px 0;
        font-size: 14px;
        opacity: 0.9;
      }

      .category-meta {
        display: flex;
        gap: 20px;
        font-size: 13px;
        opacity: 0.8;

        span {
          display: flex;
          align-items: center;
          gap: 6px;
        }
      }
    }
  }
}

.community-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  gap: 24px;

  .main-content {
    flex: 1;
    min-width: 0;
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    max-height: calc(100vh - 200px);
    display: flex;
    flex-direction: column;

    .filter-bar {
      margin-bottom: 20px;
      border-bottom: 1px solid #f0f0f0;
      flex-shrink: 0;

      ::v-deep .el-tabs__header {
        margin: 0;
      }

      ::v-deep .el-tabs__item {
        height: 44px;
        line-height: 44px;
        font-size: 15px;

        i {
          margin-left: 6px;
        }
      }
    }

    .post-list {
      flex: 1;
      overflow-y: auto;
      padding-right: 8px;

      // 隐藏滚动条但保留滚动功能
      &::-webkit-scrollbar {
        width: 0;
        height: 0;
        display: none;
      }
      -ms-overflow-style: none;
      scrollbar-width: none;

      .post-item {
        display: flex;
        padding: 20px;
        border-bottom: 1px solid #f0f0f0;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          background: #f8f9fa;
        }

        &:last-child {
          border-bottom: none;
        }

        .post-vote {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          margin-right: 16px;

          .vote-count {
            font-size: 14px;
            font-weight: 600;
            color: #333;
          }

          .el-button {
            padding: 4px;
            color: #999;

            &:hover {
              color: #ff4757;
            }
          }
        }

        .post-main {
          flex: 1;
          min-width: 0;

          .post-title {
            margin: 0 0 8px 0;
            font-size: 18px;
            font-weight: 600;
            color: #333;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }

          .post-summary {
            margin: 0 0 12px 0;
            font-size: 14px;
            color: #666;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }

          .post-meta {
            display: flex;
            gap: 16px;
            font-size: 13px;
            color: #999;

            span {
              display: flex;
              align-items: center;
              gap: 4px;
            }
          }
        }
      }

      .loading-status {
        text-align: center;
        padding: 20px;
        color: #999;
        font-size: 14px;

        i {
          margin-right: 8px;
        }
      }
    }
    }

  .sidebar {
    width: 320px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .category-card {
      background: #fff;
      border-radius: 12px;
      padding: 20px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

      .card-header {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 2px solid #f0f0f0;

        i {
          color: #667eea;
        }
      }

      .sub-categories {
        .sub-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            background: #f8f9fa;
          }

          &.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: #fff;

            i {
              color: #fff;
            }
          }

          i {
            font-size: 16px;
            color: #667eea;
          }

          span {
            font-size: 14px;
          }
        }
      }
    }
  }
}

@media (max-width: 1024px) {
  .community-content {
    flex-direction: column;

    .sidebar {
      width: 100%;
    }
  }
}
</style>
